export type GitSkillSource = {
  remoteUrl: string;
  ref?: string;
  subpath?: string;
};

type GitSkillSourceIdentity = {
  remoteUrl: string;
  ref?: string | null;
  subpath?: string | null;
};

export function isSameGitSkillSource(
  first: GitSkillSourceIdentity,
  second: GitSkillSourceIdentity
): boolean {
  return (
    first.remoteUrl === second.remoteUrl &&
    (first.ref ?? null) === (second.ref ?? null) &&
    (first.subpath ?? null) === (second.subpath ?? null)
  );
}

function optionalValue(value?: string | null): string | undefined {
  return value?.trim() || undefined;
}

function cleanSubpath(value?: string | null): string | undefined {
  const path = optionalValue(value)?.replace(/^\/+|\/+$/g, "");
  if (!path) return undefined;
  if (
    path.includes("\\") ||
    path.includes("\0") ||
    path.split("/").some((part) => !part || part === "." || part === "..")
  ) {
    throw new Error("Repository path must be a relative Git path");
  }
  return path;
}

export function parseGitSkillSource(input: {
  remoteUrl: string;
  ref?: string | null;
  subpath?: string | null;
}): GitSkillSource {
  let url: URL;
  try {
    url = new URL(input.remoteUrl.trim());
  } catch {
    throw new Error("Enter an HTTPS Git clone URL");
  }
  if (url.protocol !== "https:") {
    throw new Error("Only HTTPS Git clone URLs are supported");
  }
  if (url.username || url.password) {
    throw new Error("Repository URLs cannot contain credentials");
  }
  if (url.search || url.hash) {
    throw new Error("Repository URLs cannot contain a query or fragment");
  }
  if (url.pathname === "/" || url.pathname === "") {
    throw new Error("Repository URL must include a repository path");
  }
  url.pathname = url.pathname.replace(/\/+$/, "");

  const ref = optionalValue(input.ref);
  if (ref?.includes("\0")) throw new Error("Invalid Git ref");
  const subpath = cleanSubpath(input.subpath);
  return {
    remoteUrl: url.toString(),
    ...(ref && { ref }),
    ...(subpath && { subpath }),
  };
}
